<template>
  <div>
    <b-modal
      id="welcomeModal"
      ref="welcomeModal"
      size="xl"
      hide-footer
      centered
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      hide-header-close
      class="welcome-modal"
    >
      <div class="welcome-content">
        <!-- Header com logo e título -->
        <div class="welcome-header">
          <div class="logo-container">
            <img src="@/assets/logo_express_white.png" alt="Logo" class="welcome-logo" />
          </div>
          <h2 class="welcome-title">Bem-vindo à Nova Versão!</h2>
          <p class="welcome-subtitle">{{ versionInfo.fullVersion }} - {{ versionInfo.codename }}</p>
        </div>

        <!-- Conteúdo principal -->
        <div class="welcome-body">
          <!-- Seção de Novidades -->
          <div class="new-features-section">
            <h3 class="section-title">
              <i class="fas fa-star"></i>
              Novidades desta Versão
            </h3>

            <b-carousel
              v-model="currentFeatureIndex"
              :interval="5000"
              controls
              indicators
              background="transparent"
              img-width="1200"
              img-height="260"
              class="features-b-carousel"
            >
              <b-carousel-slide v-for="(feature, index) in newFeatures" :key="index" img-blank img-alt="feature">
                <div class="feature-item">
                  <div class="feature-icon">
                    <i :class="feature.icon"></i>
                  </div>
                  <div class="feature-content">
                    <h4 class="feature-title">{{ feature.title }}</h4>
                    <p class="feature-description">{{ feature.description }}</p>
                  </div>
                </div>
              </b-carousel-slide>
            </b-carousel>
          </div>

          <!-- Seção de Melhorias -->
          <div class="improvements-section">
            <h3 class="section-title">
              <i class="fas fa-tools"></i>
              Melhorias e Correções
            </h3>

            <div class="improvements-list">
              <div v-for="(improvement, index) in improvements" :key="index" class="improvement-item">
                <i class="fas fa-check-circle"></i>
                <span>{{ improvement }}</span>
              </div>
            </div>
          </div>

          <!-- Seção de Tutorial -->
          <div v-if="tutorialSteps.length > 0" class="tutorial-section">
            <h3 class="section-title">
              <i class="fas fa-graduation-cap"></i>
              Guia da Dashboard
            </h3>

            <div class="tutorial-navigation top">
              <button class="tutorial-btn prev-btn" @click="previousTutorialStep" :disabled="currentTutorialStep === 0">
                <i class="fas fa-chevron-left"></i>
                Anterior
              </button>
              <button class="tutorial-btn next-btn" @click="nextTutorialStep" :disabled="currentTutorialStep === tutorialSteps.length - 1">
                Próximo
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>

            <div class="tutorial-content tutorial-carousel">
              <div class="carousel-track" :style="{ transform: 'translateX(-' + currentTutorialStep * 100 + '%)' }">
                <div v-for="(step, index) in tutorialSteps" :key="index" class="tutorial-step" :class="{ active: currentTutorialStep === index }">
                  <div class="step-number">{{ index + 1 }}</div>
                  <div class="step-content">
                    <h4 class="step-title">{{ step.title }}</h4>
                    <p class="step-description">{{ step.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer com botões -->
        <div class="welcome-footer">
          <div class="footer-actions">
            <button class="action-btn primary-btn" @click="startUsing">
              <i class="fas fa-rocket"></i>
              Começar a Usar
            </button>
          </div>

          <div class="footer-info">
            <p class="release-info">
              <i class="fas fa-calendar"></i>
              Lançado em {{ formatDate(versionInfo.releaseDate) }}
            </p>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { getVersionInfo } from '@/config/version.js'
import { getVersionSpecificWelcome } from '@/utils/welcomeContent.js'

export default {
  name: 'WelcomeModal',

  data() {
    return {
      currentTutorialStep: 0,
      versionInfo: getVersionInfo(),
      welcomeContent: null,
      currentFeatureIndex: 0,
      featureIntervalId: null
    }
  },

  computed: {
    newFeatures() {
      return this.welcomeContent?.newFeatures || []
    },
    improvements() {
      return this.welcomeContent?.improvements || []
    },
    tutorialSteps() {
      return this.welcomeContent?.tutorialSteps || []
    }
  },

  methods: {
    initializeWelcomeContent() {
      const currentVersion = this.versionInfo.version
      this.welcomeContent = getVersionSpecificWelcome(currentVersion)
    },

    show() {
      this.$refs.welcomeModal.show()
    },

    hide() {
      this.$refs.welcomeModal.hide()
    },

    startUsing() {
      this.markAsViewed()
      this.hide()
    },

    skipTutorial() {
      this.markAsViewed()
      this.hide()
    },

    markAsViewed() {
      const currentVersion = this.versionInfo.version
      const viewedVersions = this.getViewedVersions()

      if (!viewedVersions.includes(currentVersion)) {
        viewedVersions.push(currentVersion)
        localStorage.setItem('welcomeModalViewedVersions', JSON.stringify(viewedVersions))
      }
    },

    getViewedVersions() {
      try {
        const stored = localStorage.getItem('welcomeModalViewedVersions')
        return stored ? JSON.parse(stored) : []
      } catch (error) {
        return []
      }
    },

    nextTutorialStep() {
      if (this.currentTutorialStep < this.tutorialSteps.length - 1) {
        this.currentTutorialStep++
      }
    },

    previousTutorialStep() {
      if (this.currentTutorialStep > 0) {
        this.currentTutorialStep--
      }
    },

    formatDate(dateString) {
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('pt-BR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },

    shouldShow() {
      const currentVersion = this.versionInfo.version
      const viewedVersions = this.getViewedVersions()
      if (viewedVersions.includes(currentVersion)) return false
      const expiry = this.welcomeContent && this.welcomeContent.expiryDate
      if (expiry) {
        const now = new Date()
        const expiryDate = new Date(expiry)
        if (!isNaN(expiryDate.getTime())) {
          expiryDate.setHours(23, 59, 59, 999)
          if (now > expiryDate) return false
        }
      }
      const expiryDays = this.welcomeContent && this.welcomeContent.expiryDays
      if (expiryDays) {
        const now = new Date()
        const base = this.versionInfo && this.versionInfo.releaseDate ? new Date(this.versionInfo.releaseDate) : new Date()
        if (!isNaN(base.getTime())) {
          const cutoff = new Date(base)
          cutoff.setDate(cutoff.getDate() + Number(expiryDays))
          cutoff.setHours(23, 59, 59, 999)
          if (now > cutoff) return false
        }
      }
      return true
    }
  },

  mounted() {
    this.initializeWelcomeContent()
    if (this.shouldShow()) {
      this.$nextTick(() => {
        this.show()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.welcome-modal {
  .modal-dialog {
    max-width: 1200px;
  }

  .modal-content {
    border: none;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }
}

.welcome-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  min-height: 520px;
  animation: launchPop 600ms ease-out;
}

.welcome-header {
  text-align: center;
  padding: 40px 30px 30px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);

  .logo-container {
    margin-bottom: 20px;

    .welcome-logo {
      height: 60px;
      width: auto;
      filter: brightness(0) invert(1);
    }
  }

  .welcome-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 10px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .welcome-subtitle {
    font-size: 1.2rem;
    opacity: 0.9;
    margin: 0;
    font-weight: 300;
  }
}

.welcome-body {
  padding: 30px;
  max-height: 420px;
  overflow-y: auto;

  .section-title {
    font-size: 1.4rem;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #ffd700;
    }
  }

  .new-features-section {
    margin-bottom: 30px;
    .features-navigation {
      margin-bottom: 12px;
      display: flex;
      justify-content: space-between;
      .tutorial-btn {
        background: rgba(255, 255, 255, 0.95);
        color: #1f2937;
        border: 2px solid rgba(0, 0, 0, 0.6);
        border-radius: 6px;
        padding: 8px 14px;
        font-weight: 600;
      }
      .tutorial-btn:disabled {
        opacity: 0.5;
      }
    }
    .features-b-carousel {
      margin-bottom: 18px;
    }

    .features-carousel {
      overflow: hidden;
    }

    .feature-item {
      display: flex;
      align-items: flex-start;
      gap: 18px;
      padding: 28px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.08));
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-radius: 16px;
      backdrop-filter: blur(8px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
      animation: featureIn 450ms ease both;
    }

    .feature-icon {
      flex-shrink: 0;
      width: 60px;
      height: 60px;
      background: linear-gradient(145deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.15));
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(255, 255, 255, 0.3);
      box-shadow: inset 0 2px 6px rgba(255, 255, 255, 0.25), 0 6px 14px rgba(0, 0, 0, 0.15);
    }

    .feature-icon i {
      font-size: 1.6rem;
      color: #ffd700;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
    }

    .feature-content .feature-title {
      font-size: 1.35rem;
      font-weight: 700;
      letter-spacing: 0.2px;
      margin-bottom: 8px;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }

    .feature-content .feature-description {
      font-size: 1.06rem;
      opacity: 0.95;
      margin: 0;
      line-height: 1.6;
    }

    ::v-deep(.features-b-carousel .carousel-indicators) {
      bottom: -6px;
    }
    ::v-deep(.features-b-carousel .carousel-indicators li) {
      width: 22px;
      height: 6px;
      border-radius: 6px;
      background-color: rgba(255, 255, 255, 0.35);
      border: none;
      transition: all 0.2s ease;
      margin: 0 4px;
    }
    ::v-deep(.features-b-carousel .carousel-indicators .active) {
      background: linear-gradient(90deg, #ffd700, #ffed4e);
      box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
      transform: scale(1.08);
    }

    ::v-deep(.features-b-carousel .carousel-control-prev),
    ::v-deep(.features-b-carousel .carousel-control-next) {
      width: 60px;
      filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.35));
    }
    ::v-deep(.features-b-carousel .carousel-control-prev-icon),
    ::v-deep(.features-b-carousel .carousel-control-next-icon) {
      filter: invert(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
    }
  }

  @keyframes launchPop {
    0% {
      transform: scale(0.95) translateY(10px);
      opacity: 0;
    }
    100% {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @keyframes featureIn {
    0% {
      transform: translateY(10px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  ::v-deep(.modal-backdrop.show) {
    background-color: rgba(0, 0, 0, 0.5) !important;
    -webkit-backdrop-filter: blur(4px) saturate(80%) contrast(90%);
    backdrop-filter: blur(4px) saturate(80%) contrast(90%);
  }

  ::v-deep(body.modal-open #app) {
    filter: blur(2.5px) brightness(0.75) saturate(85%);
    transition: filter 180ms ease;
  }

  .improvements-section {
    margin-bottom: 30px;

    .improvements-list {
      display: grid;
      gap: 10px;

      .improvement-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 15px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        backdrop-filter: blur(5px);

        i {
          color: #4ade80;
          font-size: 1rem;
        }

        span {
          font-size: 0.95rem;
          opacity: 0.9;
        }
      }
    }
  }

  .tutorial-section {
    .tutorial-navigation.top {
      margin-bottom: 12px;
    }
    .tutorial-content {
      margin-bottom: 20px;

      .tutorial-step {
        display: flex;
        align-items: flex-start;
        gap: 15px;
        padding: 15px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        margin-bottom: 10px;
        opacity: 0.6;
        transition: all 0.3s ease;

        &.active {
          opacity: 1;
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.02);
        }

        .step-number {
          flex-shrink: 0;
          width: 30px;
          height: 30px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .step-content {
          flex: 1;

          .step-title {
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 5px;
          }

          .step-description {
            font-size: 0.95rem;
            opacity: 0.9;
            margin: 0;
            line-height: 1.4;
          }
        }
      }
    }

    .tutorial-navigation {
      display: flex;
      justify-content: space-between;
      gap: 15px;

      .tutorial-btn {
        padding: 10px 20px;
        border: none;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.2);
        color: white;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 8px;

        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
    .tutorial-carousel {
      overflow: hidden;
      .carousel-track {
        display: flex;
        width: 100%;
        transition: transform 0.35s ease-in-out;
      }
      .tutorial-step {
        min-width: 100%;
        margin-bottom: 0;
      }
    }
  }
}

.welcome-footer {
  padding: 20px 30px;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);

  .footer-actions {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 15px;

    .action-btn {
      padding: 12px 24px;
      border: none;
      border-radius: 25px;
      font-weight: 600;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 8px;

      &.primary-btn {
        background: linear-gradient(45deg, #ffd700, #ffed4e);
        color: #333;
        box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
        }
      }

      &.secondary-btn {
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.3);

        &:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }
      }
    }
  }

  .footer-info {
    text-align: center;

    .release-info {
      font-size: 0.9rem;
      opacity: 0.8;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      i {
        color: #ffd700;
      }
    }
  }
}

@media (max-width: 768px) {
  .welcome-modal .modal-dialog {
    margin: 10px;
    max-width: calc(100% - 20px);
  }

  .welcome-header {
    padding: 30px 20px 20px;

    .welcome-title {
      font-size: 2rem;
    }

    .welcome-subtitle {
      font-size: 1rem;
    }
  }

  .welcome-body {
    padding: 20px;
    max-height: 400px;

    .features-list .feature-item {
      flex-direction: column;
      text-align: center;

      .feature-icon {
        align-self: center;
      }
    }
  }

  .welcome-footer {
    padding: 15px 20px;

    .footer-actions {
      flex-direction: column;
      align-items: center;

      .action-btn {
        width: 100%;
        max-width: 200px;
        justify-content: center;
      }
    }
  }
}
</style>
